/**
 * Dashboard & Advanced Analytics API
 */
import type { ItemResponse, ListResponse } from '../../types/api.js';
import type { DashboardStats, RevenueChartDataPoint, TopProduct, Segment } from '../../types/models.js';
import { apiRequest } from './core.js';

export const dashboardApi = {
  getStats: (days?: number, start?: string, end?: string, segmentId?: string) => {
    const params = new URLSearchParams();
    if (start && end) { params.append('start', start); params.append('end', end); }
    else if (days) { params.append('days', String(days)); }
    // B2 (Hub audience scope): narrows customer-scoped stats to a Segment's members.
    if (segmentId) { params.append('segment', segmentId); }
    const query = params.toString();
    return apiRequest<ItemResponse<DashboardStats>>(`/dashboard/stats${query ? `?${query}` : ''}`);
  },
  getRevenueChart: (start?: string, end?: string, days?: number) => {
    const params = new URLSearchParams();
    if (start && end) { params.append('start', start); params.append('end', end); }
    else if (days) { params.append('days', String(days)); }
    const query = params.toString();
    return apiRequest<ListResponse<RevenueChartDataPoint>>(`/dashboard/revenue-chart${query ? `?${query}` : ''}`);
  },
  getTopProducts: (limit?: number) => apiRequest<ListResponse<TopProduct>>(`/dashboard/top-products${limit ? `?limit=${limit}` : ''}`),
  getSegments: () => apiRequest<ListResponse<Segment>>('/dashboard/segments'),
  getFunnelStatus: () => apiRequest<ItemResponse<FunnelStatusMap>>('/dashboard/funnel-status')
};

/** Backing-automation state per funnel card key (W3 truthful pills). */
export type FunnelBackingState = 'live' | 'draft' | 'none' | 'na';

export interface FunnelBacking {
  state: FunnelBackingState;
  flowId: string | null;
  /** 'connection' = the `email_sending` entry: the sending domain's real status (V37). */
  kind: 'chat' | 'email' | 'social' | 'analytics' | 'connection';
  /** On a live `email_sending` entry: the workspace's own domain or BuzzBit's shared one (F11). */
  via?: 'own_domain' | 'shared_domain';
}

export type FunnelStatusMap = Record<string, FunnelBacking>;
